<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import coachService from '../services/coach.service'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const coach = ref<any>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const recentAttendance = ref<any[]>([])
const loadingAttendance = ref(false)
const attendanceStats = ref<any>(null)
const loadingStats = ref(false)

// Selected month for stats (default to current month)
const selectedMonth = ref<string>('')

// Pagination for attendance
const perPage = ref(25)
const currentPage = ref(1)
const lastPage = ref(1)
const totalEvents = ref(0)

// Get club UUID and coach UUID
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)
const coachUuid = computed(() => route.params.uuid as string)

// Visible pages for pagination
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Get current person role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Get possible categories from localStorage
const possibleCategories = computed(() => authStore.initData?.possible_categories || [])

// Check if category is accessible
const isCategoryAccessible = (categoryUuid: string) => {
  // OWNER and SUPERVISOR can access all categories
  if (currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR') {
    return true
  }
  // Other roles check possible_categories
  return possibleCategories.value.includes(categoryUuid)
}

// Check if coach has any accessible category (for COACH role only)
const hasAccessibleCategory = computed(() => {
  // OWNER and SUPERVISOR can access everything
  if (currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR') {
    return true
  }

  // For COACH role, check if coach has any category in common with possible_categories
  if (currentRole.value === 'COACH' && coach.value?.categories) {
    return coach.value.categories.some((category: any) =>
      possibleCategories.value.includes(category.uuid)
    )
  }

  return true
})

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canUpdate = computed(() => hasPermission('COACH_UPDATE'))

// Calculate age from birth date
const age = computed(() => {
  if (!coach.value?.birth_date) return null

  const birthDate = new Date(coach.value.birth_date)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
})

// Fetch coach data
const fetchCoach = async () => {
  if (!clubUuid.value || !coachUuid.value) {
    error.value = t('coachDetail.errorNoClubOrCoach')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await coachService.getCoach(clubUuid.value, coachUuid.value)
    coach.value = response.coach
  } catch (err: any) {
    console.error('Error fetching coach:', err)
    error.value = err.response?.data?.message || t('coachDetail.errorFetchingCoach')

    // Redirect to coaches list if not found
    if (err.response?.status === 404) {
      toastStore.error(t('coachDetail.coachNotFound'))
      router.push('/coaches')
    }
  } finally {
    loading.value = false
  }
}

// Fetch recent attendance with pagination
const fetchRecentAttendance = async (page: number = 1) => {
  if (!clubUuid.value || !coachUuid.value) return

  loadingAttendance.value = true

  try {
    const response = await coachService.getRecentAttendance(clubUuid.value, coachUuid.value, perPage.value, page)
    recentAttendance.value = response.data || []
    currentPage.value = response.current_page || page
    lastPage.value = response.last_page || 1
    totalEvents.value = response.total || 0
  } catch (err: any) {
    console.error('Error fetching recent attendance:', err)
  } finally {
    loadingAttendance.value = false
  }
}

// Handle page change
const changePage = (page: number) => {
  currentPage.value = page
  fetchRecentAttendance(page)
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

// Handle per page change
const changePerPage = () => {
  currentPage.value = 1
  fetchRecentAttendance(1)
}

// Fetch attendance stats for selected month
const fetchAttendanceStats = async () => {
  if (!clubUuid.value || !coachUuid.value) return

  loadingStats.value = true

  try {
    // Use selected month or convert YYYY-MM to YYYY-MM-DD format (first day of month)
    const dateString = selectedMonth.value ? `${selectedMonth.value}-01` : new Date().toISOString().split('T')[0]

    const response = await coachService.getAttendanceStats(clubUuid.value, coachUuid.value, dateString)
    attendanceStats.value = response
  } catch (err: any) {
    console.error('Error fetching attendance stats:', err)
  } finally {
    loadingStats.value = false
  }
}

// Handle month change
const onMonthChange = () => {
  fetchAttendanceStats()
}

// Initialize selected month to current month
const initializeMonth = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  selectedMonth.value = `${year}-${month}`
}

// Format date
const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('pl-PL')
}

// Resend invitation
const resendInvitation = async () => {
  if (!coach.value || !clubUuid.value) return

  try {
    await coachService.resendInvitation(clubUuid.value, coach.value.uuid)
    toastStore.success(t('coachDetail.invitationSent'))
  } catch (err: any) {
    console.error('Error resending invitation:', err)
    toastStore.error(err.response?.data?.message || t('coachDetail.errorSendingInvitation'))
  }
}

// Copy to clipboard
const copyToClipboard = async (text: string, type: 'email' | 'phone') => {
  if (!text) return

  try {
    await navigator.clipboard.writeText(text)
    const message = type === 'email' ? t('coachDetail.emailCopied') : t('coachDetail.phoneCopied')
    toastStore.success(message)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

// Format datetime to readable format
const formatDateTime = (dateTimeString: string | null) => {
  if (!dateTimeString) return '-'
  const date = new Date(dateTimeString)
  return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
}

onMounted(async () => {
  initializeMonth()
  await fetchCoach()

  // Only fetch stats and attendance if coach has accessible category
  if (hasAccessibleCategory.value) {
    fetchRecentAttendance()
    fetchAttendanceStats()
  }
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/coaches" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('coachDetail.backToList') }}
        </router-link>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('coachDetail.loading') }}</p>
      </div>

      <!-- Error Message -->
      <div v-else-if="error && !coach" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Coach Data -->
      <template v-else-if="coach">
        <!-- Header with Actions -->
        <div class="mb-4 flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ coach.first_name }} {{ coach.last_name }}</h1>
            <p v-if="age !== null" class="text-sm text-gray-600">{{ age }} {{ t('coachDetail.yearsOld') }}</p>
          </div>
          <div v-if="canUpdate" class="flex gap-2">
            <router-link
              :to="`/coaches/${coach.uuid}/edit`"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600"
            >
              {{ t('coachDetail.edit') }}
            </router-link>
          </div>
        </div>

        <!-- Registration Status Banner - Only for OWNER and SUPERVISOR -->
        <template v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'">
          <div v-if="coach.registration_status === 'registered'" class="bg-green-50 border-2 border-green-300 rounded p-3 mb-4">
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-sm font-medium text-green-800">{{ t('coachDetail.userRegistered') }}</span>
              <span v-if="coach.registered_at" class="text-xs text-green-700 ml-auto">{{ formatDate(coach.registered_at) }}</span>
            </div>
          </div>
          <div v-else class="bg-red-50 border-2 border-red-300 rounded p-3 mb-4">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <span class="text-sm font-medium text-red-800">{{ t('coachDetail.userNotRegistered') }}</span>
              </div>
              <button
                v-if="coach.email && coach.email !== '-'"
                @click="resendInvitation"
                class="px-3 py-1.5 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700"
              >
                {{ t('coachDetail.sendInvitation') }}
              </button>
              <p v-else class="text-xs text-red-600 font-medium">{{ t('coachDetail.noEmail') }}</p>
            </div>
          </div>
        </template>

        <!-- Coach Data - 3 columns -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <!-- Column 1: Basic Info -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('coachDetail.basicData') }}</h2>
              <div class="flex gap-4">
                <!-- Photo -->
                <div class="flex-shrink-0">
                  <div v-if="coach.photo_path" class="w-20 h-20 rounded overflow-hidden">
                    <img :src="coach.photo_path" :alt="`${coach.first_name} ${coach.last_name}`" class="w-full h-full object-cover">
                  </div>
                  <div v-else class="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                    <svg class="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <!-- Data -->
                <div class="flex-1 space-y-2 text-sm">
                  <div>
                    <span class="text-gray-600">{{ t('coachDetail.birthDate') }}</span>
                    <span class="ml-2 font-medium">{{ formatDate(coach.birth_date) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="coach.categories && coach.categories.length > 0" class="mt-3 text-sm">
                <span class="text-gray-600">{{ t('coachDetail.categories') }}</span>
                <div class="mt-1 flex flex-wrap gap-1">
                  <template v-for="category in coach.categories" :key="category.uuid">
                    <router-link
                      v-if="isCategoryAccessible(category.uuid)"
                      :to="`/categories/${category.uuid}`"
                      class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 transition-colors cursor-pointer"
                    >
                      {{ category.name }}
                    </router-link>
                    <span
                      v-else
                      class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded opacity-80"
                    >
                      {{ category.name }}
                    </span>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Column 2: Contact -->
          <div class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('coachDetail.contactData') }}</h2>
              <div class="space-y-2 text-sm">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-gray-600">{{ t('coachDetail.email') }}</span>
                    <span class="ml-2 font-medium">{{ coach.email || '-' }}</span>
                  </div>
                  <button
                    v-if="coach.email"
                    @click="copyToClipboard(coach.email, 'email')"
                    class="ml-2 p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    :title="t('coachDetail.copyEmail')"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-gray-600">{{ t('coachDetail.phone') }}</span>
                    <span class="ml-2 font-medium">{{ coach.phone || '-' }}</span>
                  </div>
                  <button
                    v-if="coach.phone"
                    @click="copyToClipboard(coach.phone, 'phone')"
                    class="ml-2 p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    :title="t('coachDetail.copyPhone')"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
                <div>
                  <span class="text-gray-600">{{ t('coachDetail.licenseNumber') }}</span>
                  <span class="ml-2 font-medium">{{ coach.coach_license_number || '-' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Column 3: Stats -->
          <div v-if="hasAccessibleCategory" class="space-y-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <div class="flex items-center justify-between mb-3">
                <h2 class="text-base font-semibold text-gray-900">{{ t('coachDetail.statistics') }}</h2>
                <input
                  type="month"
                  v-model="selectedMonth"
                  @change="onMonthChange"
                  class="px-2 py-1 text-xs border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                />
              </div>
              <div class="space-y-3">
                <div class="text-center p-3 bg-blue-50 rounded">
                  <div v-if="loadingStats" class="text-2xl font-bold text-blue-600">
                    <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                  <div v-else class="text-2xl font-bold text-blue-600">{{ attendanceStats?.total_events || 0 }}</div>
                  <div class="text-xs text-gray-600">{{ t('coachDetail.eventsSelectedMonth') }}</div>
                </div>
                <div class="text-center p-3 bg-green-50 rounded">
                  <div class="text-2xl font-bold text-green-600">{{ coach.categories?.length || 0 }}</div>
                  <div class="text-xs text-gray-600">{{ t('coachDetail.assignedCategories') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Attendance List with Pagination -->
        <div v-if="hasAccessibleCategory" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
          <div class="mb-3">
            <h2 class="text-base font-semibold text-gray-900">{{ t('coachDetail.attendanceList') }}</h2>
          </div>

          <!-- Loading state -->
          <div v-if="loadingAttendance" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            <p class="mt-2 text-sm text-gray-600">{{ t('coachDetail.loadingAttendance') }}</p>
          </div>

          <!-- No data -->
          <div v-else-if="recentAttendance.length === 0" class="text-center py-8">
            <p class="text-sm text-gray-600">{{ t('coachDetail.noRecentAttendance') }}</p>
          </div>

          <!-- Attendance list -->
          <div v-else>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div v-for="event in recentAttendance" :key="event.uuid" class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50">
                <div class="flex-1 min-w-0">
                  <router-link :to="`/events/${event.uuid}`" class="text-sm text-gray-900 hover:underline truncate block">
                    {{ event.title }}
                  </router-link>
                  <div class="text-xs text-gray-500">{{ formatDateTime(event.start) }}</div>
                  <div v-if="event.place" class="text-xs text-gray-500">{{ event.place }}</div>
                  <div v-if="!event.is_present && event.absence_reason" class="text-xs text-red-600 italic">{{ event.absence_reason }}</div>
                </div>
                <div class="ml-2 flex-shrink-0">
                  <span v-if="event.is_present" class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded border border-green-300">{{ t('coachDetail.present') }}</span>
                  <span v-else class="px-2 py-1 bg-red-100 text-red-800 text-xs rounded border border-red-300">{{ t('coachDetail.absent') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="hasAccessibleCategory && !loadingAttendance && totalEvents > 0" class="mt-4 flex justify-end items-center gap-4">
          <div class="flex items-center gap-2">
            <label for="perPage" class="text-sm text-gray-700">{{ t('coachDetail.eventsPerPage') }}:</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </template>
    </div>
  </main>
</template>
